<template>
  <section class="products-page">
    <header class="products-page__header">
      <h1>{{ $t('productsPage.title') }}</h1>
    </header>

    <main class="products-page__main">
      <div class="products-page__search-wrap">
        <div
          class="products-page__search"
          :class="{
            'is-focused': searchActive,
            'is-expanded': searchPanelVisible
          }"
        >
          <Icon class="products-page__search-icon" name="search" aria-hidden="true" />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            :placeholder="$t('productsPage.searchPlaceholder')"
            :aria-label="$t('productsPage.searchPlaceholder')"
            autocomplete="off"
            enterkeyhint="search"
            @focus="onSearchFocus"
            @blur="onSearchBlur"
            @keyup.enter="submitSearch"
          >
          <button
            v-if="showSearchAction"
            class="products-page__search-action"
            type="button"
            :aria-label="searchQuery.trim() ? $t('productsPage.clearSearch') : $t('productsPage.closeSearch')"
            @mousedown.prevent
            @click="handleSearchAction"
          >
            <Icon name="cross" aria-hidden="true" />
          </button>
        </div>

        <div
          v-if="searchPanelVisible"
          class="products-page__search-panel"
          @mousedown.prevent
        >
          <section v-if="visibleSearchHistories.length" class="products-page__panel-section">
            <div class="products-page__panel-heading">
              <h2>{{ $t('productsPage.recentSearches') }}</h2>
              <button type="button" @click="clearSearchHistories">
                {{ $t('productsPage.clearAll') }}
              </button>
            </div>
            <div
              v-for="(history, index) in visibleSearchHistories"
              :key="`${history}-${index}`"
              class="products-page__history-item"
            >
              <button
                class="products-page__history-select"
                type="button"
                @click="selectSearchHistory(history)"
              >
                <Icon name="underway-o" aria-hidden="true" />
                <span>{{ history }}</span>
              </button>
              <button
                class="products-page__history-delete"
                type="button"
                :aria-label="$t('productsPage.deleteHistory')"
                @click="deleteSearchHistory(history)"
              >
                <Icon name="cross" aria-hidden="true" />
              </button>
            </div>
          </section>

          <div
            v-if="visibleSearchHistories.length && recommendations.length"
            class="products-page__panel-divider"
          ></div>

          <section v-if="recommendations.length" class="products-page__panel-section">
            <div class="products-page__panel-heading">
              <h2>{{ $t('productsPage.recommendedForYou') }}</h2>
            </div>
            <button
              v-for="(item, index) in recommendations"
              :key="item.id || item.goodsId || index"
              class="products-page__recommendation"
              type="button"
              @click="openProduct(item)"
            >
              <span class="products-page__recommendation-image">
                <img v-if="item.imgUrl" v-lazy="item.imgUrl" :alt="item.name">
              </span>
              <span class="products-page__recommendation-content">
                <strong>{{ item.name }}</strong>
                <span class="products-page__recommendation-price">
                  <span>{{ $t('productCommon.firstRentalAmount') }}</span>
                  <b>{{ recommendationPrice(item) }}</b>
                </span>
              </span>
              <Icon class="products-page__recommendation-arrow" name="arrow" aria-hidden="true" />
            </button>
          </section>
        </div>
      </div>

      <div class="products-page__content">
        <div
          class="products-page__benefits"
          @mousedown="closeSearchPanel"
          @touchstart="closeSearchPanel"
        >
          <DeliveryBenefitBanner />
        </div>

        <div
          ref="productScroll"
          class="products-page__scroll"
          @mousedown="closeSearchPanel"
          @touchstart="closeSearchPanel"
          @scroll.passive="handleProductScroll"
        >
          <van-pull-refresh
            v-model="refreshing"
            class="products-page__pull-refresh"
            :pulling-text="$t('loading')"
            :loosing-text="$t('loading')"
            :loading-text="$t('loading')"
            @refresh="refreshProducts"
          >
            <div class="products-page__scroll-content">
              <ProductPageState
                v-if="pageStateType"
                :type="pageStateType"
              />

              <template v-else>
                <div ref="productsGrid" class="products-page__grid">
                  <ProductMiniCard
                    v-for="(item, index) in visibleProducts"
                    :key="item.id || item.goodsId || index"
                    class="products-page__card"
                    :product="item"
                    :show-rental-schemes="false"
                    @click="openProduct"
                    @image-load="scheduleMasonryLayout"
                  />
                </div>
                <div v-if="loadingMore" class="products-page__load-more">
                  <ProductPageState type="loading" :compact="true" />
                </div>
              </template>
            </div>
          </van-pull-refresh>
        </div>
      </div>
    </main>

    <AppBottomNav />
    <CustomerSupportFloating />
  </section>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import { Icon, PullRefresh } from 'vant';
import imagesLoaded from 'imagesloaded';
import Masonry from 'masonry-layout';
import { getGoodsCategory, getGoodsList } from '@/api/goods';
import { LIST_TYPE } from '@/common/constants';
import {
  PRODUCT_SEARCH_HISTORY_KEY,
  filterProductsByKeyword,
  getProductDetailQuery,
  getProductPaginationHasMore,
  getProductPriceText,
  normalizeProducts,
  updateSearchHistory
} from '@/common/product';
import AppBottomNav from '@/components/AppBottomNav/index.vue';
import CustomerSupportFloating from '@/components/CustomerSupportFloating/index.vue';
import DeliveryBenefitBanner from '@/components/DeliveryBenefitBanner/index.vue';
import ProductMiniCard from '@/components/ProductMiniCard/index.vue';
import ProductPageState from '@/components/ProductPageState/index.vue';

const SEARCH_HISTORY_LIMIT = 10;
const SEARCH_HISTORY_VISIBLE_LIMIT = 3;
const LOAD_MORE_THRESHOLD = 120;

export default {
  name: 'GoodsList',
  components: {
    AppBottomNav,
    CustomerSupportFloating,
    DeliveryBenefitBanner,
    Icon,
    ProductMiniCard,
    ProductPageState,
    [PullRefresh.name]: PullRefresh
  },
  data() {
    return {
      searchQuery: '',
      searchActive: false,
      searchHistories: [],
      products: [],
      recommendations: [],
      pageNum: 1,
      pageSize: 20,
      hasMore: true,
      initialLoading: true,
      initialLoaded: false,
      initialError: false,
      requestingFirstPage: false,
      loadingMore: false,
      refreshing: false,
      recommendationLoading: false,
      listVersion: 0,
      searchBlurTimer: null,
      masonry: null,
      masonryGrid: null,
      masonryImageWatcher: null,
      masonryFrameId: null,
      masonryScheduleVersion: 0,
    };
  },
  computed: {
    ...mapState({
      productListCache: state => state.productListCache,
    }),
    visibleProducts() {
      return filterProductsByKeyword(this.products, this.searchQuery);
    },
    visibleSearchHistories() {
      return this.searchHistories.slice(0, SEARCH_HISTORY_VISIBLE_LIMIT);
    },
    isSearchBlank() {
      return this.searchQuery.trim() === '';
    },
    searchPanelVisible() {
      return this.searchActive &&
        this.isSearchBlank &&
        (this.visibleSearchHistories.length > 0 || this.recommendations.length > 0);
    },
    showSearchAction() {
      return !this.isSearchBlank || this.searchPanelVisible;
    },
    pageStateType() {
      if (this.initialLoading && this.products.length === 0) {
        return 'loading';
      }
      if (this.initialError && this.products.length === 0) {
        return 'error';
      }
      if (this.initialLoaded && this.products.length === 0) {
        return 'empty';
      }
      if (!this.isSearchBlank && this.visibleProducts.length === 0) {
        return 'search-empty';
      }
      return '';
    }
  },
  watch: {
    searchQuery() {
      this.scheduleNearEndCheck();
    },
    'products.length'() {
      this.scheduleNearEndCheck();
    },
    visibleProducts() {
      this.scheduleMasonryLayout({ reloadItems: true });
    }
  },
  mounted() {
    this.searchHistories = this.readSearchHistories();
    this.restoreProductListCache();
    this.loadInitialData();
  },
  beforeDestroy() {
    if (this.searchBlurTimer) {
      clearTimeout(this.searchBlurTimer);
    }
    this.destroyMasonry();
  },
  methods: {
    ...mapMutations('productListCache', ['SET_PRODUCT_LIST_CACHE']),
    async loadInitialData() {
      const hasCachedProducts = this.products.length > 0;
      await Promise.all([
        this.loadFirstProductPage(),
        this.loadRecommendations({ silent: hasCachedProducts })
      ]);
    },
    async loadFirstProductPage({ isRefresh = false } = {}) {
      if (this.requestingFirstPage) {
        return false;
      }

      this.requestingFirstPage = true;
      const requestVersion = ++this.listVersion;
      const hasExistingProducts = this.products.length > 0;
      if (!isRefresh && this.products.length === 0) {
        this.initialLoading = true;
      }

      try {
        const res = await getGoodsList({
          pageNum: 1,
          pageSize: this.pageSize
        });
        if (res?.code !== 'Success') {
          throw new Error(res?.message || this.$t('productState.loadFailed'));
        }

        const pageData = res.data || {};
        const records = Array.isArray(pageData.records) ? pageData.records : [];
        const normalizedRecords = normalizeProducts(records);
        if (requestVersion !== this.listVersion) {
          return false;
        }

        this.products = normalizedRecords;
        this.pageNum = 2;
        this.hasMore = getProductPaginationHasMore({
          total: pageData.total,
          loadedCount: normalizedRecords.length,
          receivedCount: records.length,
          pageSize: this.pageSize
        });
        this.initialError = false;
        this.initialLoaded = true;
        this.cacheProductList();
        this.scheduleNearEndCheck();
        return true;
      } catch (error) {
        if (!isRefresh && this.products.length === 0) {
          this.initialError = true;
          this.initialLoaded = true;
        }
        if (!hasExistingProducts || isRefresh) {
          this.$toast(error?.message || this.$t('productState.loadFailed'));
        }
        return false;
      } finally {
        if (requestVersion === this.listVersion) {
          this.requestingFirstPage = false;
          this.initialLoading = false;
        }
      }
    },
    async loadMoreProducts() {
      if (
        this.loadingMore ||
        this.requestingFirstPage ||
        this.refreshing ||
        !this.hasMore ||
        this.products.length === 0 ||
        (!this.isSearchBlank && this.visibleProducts.length === 0)
      ) {
        return;
      }

      this.loadingMore = true;
      const requestVersion = this.listVersion;
      const requestPage = this.pageNum;
      try {
        const res = await getGoodsList({
          pageNum: requestPage,
          pageSize: this.pageSize
        });
        if (res?.code !== 'Success') {
          throw new Error(res?.message || this.$t('productsPage.loadMoreError'));
        }

        if (requestVersion !== this.listVersion) {
          return;
        }
        const pageData = res.data || {};
        const records = Array.isArray(pageData.records) ? pageData.records : [];
        const normalizedRecords = normalizeProducts(records);
        const nextProducts = this.products.concat(normalizedRecords);
        this.products = nextProducts;
        this.pageNum = requestPage + 1;
        this.hasMore = getProductPaginationHasMore({
          total: pageData.total,
          loadedCount: nextProducts.length,
          receivedCount: records.length,
          pageSize: this.pageSize
        });
        this.cacheProductList();
      } catch (error) {
        this.$toast(error?.message || this.$t('productsPage.loadMoreError'));
      } finally {
        this.loadingMore = false;
      }
    },
    async loadRecommendations({ silent = false } = {}) {
      if (this.recommendationLoading) {
        return false;
      }
      this.recommendationLoading = true;
      try {
        const res = await getGoodsCategory({
          pageNum: 1,
          pageSize: 3,
          location: LIST_TYPE.SEARCH_RECOMMEND,
          showPrice: true
        });
        if (res?.code !== 'Success') {
          throw new Error(res?.message || this.$t('productsPage.recommendationLoadError'));
        }
        const records = Array.isArray(res.data?.records) ? res.data.records : [];
        this.recommendations = normalizeProducts(records).slice(0, 3);
        return true;
      } catch (error) {
        this.recommendations = [];
        if (!silent) {
          this.$toast(error?.message || this.$t('productsPage.recommendationLoadError'));
        }
        return false;
      } finally {
        this.recommendationLoading = false;
      }
    },
    async refreshProducts() {
      try {
        await this.loadFirstProductPage({ isRefresh: true });
        await this.loadRecommendations();
      } finally {
        this.refreshing = false;
        this.scheduleNearEndCheck();
      }
    },
    restoreProductListCache() {
      const cache = this.productListCache || {};
      if (
        cache.locale !== this.$i18n.locale ||
        !Array.isArray(cache.products) ||
        cache.products.length === 0
      ) {
        return false;
      }
      this.products = cache.products;
      this.pageNum = Number(cache.pageNum) > 0 ? Number(cache.pageNum) : 1;
      this.hasMore = Boolean(cache.hasMore);
      this.initialLoading = false;
      this.initialLoaded = true;
      this.initialError = false;
      return true;
    },
    cacheProductList() {
      this.SET_PRODUCT_LIST_CACHE({
        products: this.products,
        pageNum: this.pageNum,
        hasMore: this.hasMore,
        locale: this.$i18n.locale,
      });
    },
    scheduleNearEndCheck() {
      this.$nextTick(() => {
        this.checkLoadMoreAfterLayout();
      });
    },
    getMasonryGutter(grid) {
      return Number.parseFloat(window.getComputedStyle(grid).columnGap) || 0;
    },
    scheduleMasonryLayout({ reloadItems = false, watchImages = false } = {}) {
      const scheduleVersion = this.masonryScheduleVersion;
      this.$nextTick(() => {
        if (this._isBeingDestroyed || this._isDestroyed || scheduleVersion !== this.masonryScheduleVersion) {
          return;
        }
        if (this.masonryFrameId !== null) {
          window.cancelAnimationFrame(this.masonryFrameId);
        }
        this.masonryFrameId = window.requestAnimationFrame(() => {
          this.masonryFrameId = null;
          if (this._isBeingDestroyed || this._isDestroyed || scheduleVersion !== this.masonryScheduleVersion) {
            return;
          }
          const grid = this.$refs.productsGrid;
          if (!grid) {
            if (this.masonryImageWatcher) {
              this.masonryImageWatcher.off('progress', this.scheduleMasonryLayout);
              this.masonryImageWatcher.off('always', this.scheduleMasonryLayout);
              this.masonryImageWatcher = null;
            }
            if (this.masonry) {
              this.masonry.destroy();
              this.masonry = null;
            }
            this.masonryGrid = null;
            return;
          }
          if (this.masonry && this.masonryGrid !== grid) {
            this.masonry.destroy();
            this.masonry = null;
            this.masonryGrid = null;
            if (this.masonryImageWatcher) {
              this.masonryImageWatcher.off('progress', this.scheduleMasonryLayout);
              this.masonryImageWatcher.off('always', this.scheduleMasonryLayout);
              this.masonryImageWatcher = null;
            }
          }
          if (!this.masonry) {
            this.masonry = new Masonry(grid, {
              itemSelector: '.products-page__card',
              columnWidth: '.products-page__card',
              gutter: this.getMasonryGutter(grid),
              percentPosition: true,
              transitionDuration: 0,
            });
            this.masonryGrid = grid;
            watchImages = true;
          } else if (reloadItems) {
            this.masonry.reloadItems();
            watchImages = true;
          }
          this.masonry.options.gutter = this.getMasonryGutter(grid);
          this.masonry.layout();
          if (watchImages) {
            this.watchMasonryImages(grid);
          }
          this.scheduleNearEndCheck();
        });
      });
    },
    watchMasonryImages(grid) {
      if (this.masonryImageWatcher) {
        this.masonryImageWatcher.off('progress', this.scheduleMasonryLayout);
        this.masonryImageWatcher.off('always', this.scheduleMasonryLayout);
      }
      this.masonryImageWatcher = imagesLoaded(grid);
      this.masonryImageWatcher.on('progress', this.scheduleMasonryLayout);
      this.masonryImageWatcher.on('always', this.scheduleMasonryLayout);
    },
    destroyMasonry() {
      this.masonryScheduleVersion += 1;
      if (this.masonryFrameId !== null) {
        window.cancelAnimationFrame(this.masonryFrameId);
        this.masonryFrameId = null;
      }
      if (this.masonryImageWatcher) {
        this.masonryImageWatcher.off('progress', this.scheduleMasonryLayout);
        this.masonryImageWatcher.off('always', this.scheduleMasonryLayout);
        this.masonryImageWatcher = null;
      }
      if (this.masonry) {
        this.masonry.destroy();
        this.masonry = null;
      }
      this.masonryGrid = null;
    },
    checkLoadMoreAfterLayout() {
      const target = this.$refs.productScroll;
      if (
        !target ||
        target.clientHeight <= 0 ||
        this.loadingMore ||
        this.requestingFirstPage ||
        this.refreshing ||
        !this.hasMore ||
        this.products.length === 0 ||
        (!this.isSearchBlank && this.visibleProducts.length === 0)
      ) {
        return;
      }
      const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceToBottom <= LOAD_MORE_THRESHOLD) {
        this.loadMoreProducts();
      }
    },
    handleProductScroll() {
      this.closeSearchPanel();
      this.checkLoadMoreAfterLayout();
    },
    onSearchFocus() {
      if (this.searchBlurTimer) {
        clearTimeout(this.searchBlurTimer);
        this.searchBlurTimer = null;
      }
      this.searchActive = true;
    },
    onSearchBlur() {
      if (this.searchBlurTimer) {
        clearTimeout(this.searchBlurTimer);
      }
      this.searchBlurTimer = setTimeout(() => {
        this.searchActive = false;
        this.searchBlurTimer = null;
      }, 100);
    },
    handleSearchAction() {
      if (!this.isSearchBlank) {
        this.searchQuery = '';
        this.$nextTick(() => this.$refs.searchInput?.focus());
        return;
      }
      this.closeSearchPanel();
    },
    closeSearchPanel() {
      this.searchActive = false;
      this.$refs.searchInput?.blur();
    },
    submitSearch(event) {
      if (event?.isComposing) {
        return;
      }
      const nextHistories = updateSearchHistory(
        this.searchHistories,
        this.searchQuery,
        SEARCH_HISTORY_LIMIT
      );
      this.searchHistories = nextHistories;
      this.saveSearchHistories(nextHistories);
      this.closeSearchPanel();
    },
    selectSearchHistory(history) {
      this.searchQuery = history;
      const nextHistories = updateSearchHistory(
        this.searchHistories,
        history,
        SEARCH_HISTORY_LIMIT
      );
      this.searchHistories = nextHistories;
      this.saveSearchHistories(nextHistories);
      this.closeSearchPanel();
    },
    deleteSearchHistory(history) {
      const nextHistories = this.searchHistories.filter(item => item !== history);
      this.searchHistories = nextHistories;
      this.saveSearchHistories(nextHistories);
    },
    clearSearchHistories() {
      this.searchHistories = [];
      this.saveSearchHistories([]);
    },
    readSearchHistories() {
      try {
        const value = JSON.parse(window.localStorage.getItem(PRODUCT_SEARCH_HISTORY_KEY) || '[]');
        if (!Array.isArray(value)) {
          return [];
        }
        return value
          .filter(item => typeof item === 'string')
          .slice()
          .reverse()
          .reduce(
            (histories, item) => updateSearchHistory(histories, item, SEARCH_HISTORY_LIMIT),
            []
          );
      } catch (error) {
        return [];
      }
    },
    saveSearchHistories(histories) {
      try {
        window.localStorage.setItem(PRODUCT_SEARCH_HISTORY_KEY, JSON.stringify(histories));
      } catch (error) {
        // 搜索历史不可用时不影响商品浏览和搜索。
      }
    },
    recommendationPrice(product) {
      return getProductPriceText(product, this.$t('productCommon.pricePending'));
    },
    openProduct(product) {
      const query = getProductDetailQuery(product);
      if (!query) {
        return;
      }
      this.closeSearchPanel();
      this.$router.push({
        name: 'GoodsDetail',
        query
      });
    }
  }
};
</script>

<style lang="less" scoped>
.products-page {
  display: flex;
  width: 100%;
  max-width: 750px;
  height: 100vh;
  min-height: 100vh;
  margin: 0 auto;
  padding-bottom: calc(132px + env(safe-area-inset-bottom));
  overflow: hidden;
  box-sizing: border-box;
  flex-direction: column;
  background: #F4F8FF;
  color: #070D3F;
}

.products-page__header {
  display: flex;
  height: 96px;
  min-height: 96px;
  padding-top: env(safe-area-inset-top);
  align-items: center;
  justify-content: center;
  box-sizing: content-box;
  flex: 0 0 auto;
  background: #FFFFFF;

  h1 {
    overflow: hidden;
    font-size: 36px;
    font-weight: 500;
    line-height: 52px;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.products-page__main {
  display: flex;
  min-height: 0;
  padding: 16px 16px 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.products-page__search-wrap {
  position: relative;
  z-index: 20;
  flex: 0 0 auto;
}

.products-page__search {
  position: relative;
  z-index: 2;
  display: flex;
  width: 100%;
  height: 104px;
  padding: 0 12px 0 28px;
  align-items: center;
  box-sizing: border-box;
  background: #FFFFFF;
  border: 2px solid #E5EAF3;
  border-radius: 24px;

  &.is-focused {
    border-color: #0B45FF;
  }

  &.is-expanded {
    border-radius: 24px 24px 0 0;
  }

  input {
    width: 0;
    height: 100%;
    min-width: 0;
    padding: 0;
    flex: 1 1 auto;
    background: transparent;
    border: 0;
    color: #070D3F;
    font-family: inherit;
    font-size: 36px;
    font-weight: 500;
    line-height: 40px;

    &::placeholder {
      color: #9CA3AF;
      font-weight: 400;
      opacity: 1;
    }

    &::-webkit-search-cancel-button {
      display: none;
    }
  }
}

.products-page__search-icon {
  margin-right: 2px;
  color: #6B7280;
  flex: 0 0 auto;
  font-size: 44px;
}

.products-page__search-action {
  display: flex;
  width: 88px;
  height: 88px;
  margin-left: 8px;
  padding: 0;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  color: #9CA3AF;
  font-size: 40px;
}

.products-page__search-panel {
  position: absolute;
  z-index: 1;
  top: calc(100% - 2px);
  right: 0;
  left: 0;
  max-height: 62vh;
  padding: 28px 24px 20px;
  overflow-y: auto;
  box-sizing: border-box;
  background: #FFFFFF;
  border: 2px solid #E5EAF3;
  border-top: 0;
  border-radius: 0 0 24px 24px;
}

.products-page__panel-heading {
  display: flex;
  min-height: 48px;
  align-items: center;
  justify-content: space-between;

  h2 {
    min-width: 0;
    overflow: hidden;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    margin-left: 16px;
    padding: 8px 0;
    flex: 0 0 auto;
    background: transparent;
    border: 0;
    color: #0B45FF;
    font-family: inherit;
    font-size: 22px;
    font-weight: 500;
    line-height: 30px;
  }
}

.products-page__history-item {
  display: flex;
  width: 100%;
  height: 68px;
  align-items: center;
}

.products-page__history-select {
  display: flex;
  min-width: 0;
  height: 100%;
  padding: 0;
  align-items: center;
  overflow: hidden;
  flex: 1 1 auto;
  background: transparent;
  border: 0;
  color: #070D3F;
  font-family: inherit;
  text-align: left;

  > .van-icon {
    color: #6B7280;
    flex: 0 0 auto;
    font-size: 44px;
  }

  > span {
    min-width: 0;
    margin-left: 20px;
    overflow: hidden;
    flex: 1 1 auto;
    font-size: 26px;
    font-weight: 400;
    line-height: 36px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.products-page__history-delete {
  display: flex;
  width: 68px;
  height: 68px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0;
  background: transparent;
  border: 0;
  color: #9CA3AF;
  font-size: 56px;
}

.products-page__panel-divider {
  height: 2px;
  margin: 16px 0;
  background: #E5EAF3;
}

.products-page__recommendation {
  display: flex;
  width: 100%;
  height: 116px;
  margin-top: 16px;
  padding: 16px 20px;
  align-items: center;
  box-sizing: border-box;
  background: #F8FAFF;
  border: 2px solid #E5EAF3;
  border-radius: 20px;
  color: #070D3F;
  font-family: inherit;
  text-align: left;

  &:first-of-type {
    margin-top: 20px;
  }
}

.products-page__recommendation-image {
  display: flex;
  width: 84px;
  height: 84px;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: #FFFFFF;
  border-radius: 16px;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.products-page__recommendation-content {
  display: flex;
  min-width: 0;
  margin-left: 20px;
  overflow: hidden;
  flex: 1 1 auto;
  flex-direction: column;

  strong {
    overflow: hidden;
    font-size: 26px;
    font-weight: 500;
    line-height: 36px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.products-page__recommendation-price {
  display: flex;
  min-width: 0;
  align-items: center;
  font-size: 22px;
  line-height: 30px;
  white-space: nowrap;

  > span {
    overflow: hidden;
    color: #9CA3AF;
    text-overflow: ellipsis;
  }

  b {
    margin-left: 8px;
    overflow: hidden;
    color: #0B45FF;
    font-weight: 500;
    text-overflow: ellipsis;
  }
}

.products-page__recommendation-arrow {
  margin-left: 8px;
  color: #9CA3AF;
  flex: 0 0 auto;
  font-size: 44px;
}

.products-page__content {
  position: relative;
  z-index: 1;
  display: flex;
  min-height: 0;
  margin-top: 16px;
  flex: 1 1 auto;
  flex-direction: column;
}

.products-page__benefits {
  flex: 0 0 auto;
}

.products-page__scroll {
  min-height: 0;
  margin-top: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1 1 auto;
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}

.products-page__pull-refresh,
.products-page__scroll-content {
  min-height: 100%;
}

.products-page__scroll-content {
  padding-bottom: 16px;
  box-sizing: border-box;
}

.products-page__grid {
  --products-page-column-gap: 16px;
  position: relative;
  width: 100%;
  column-gap: var(--products-page-column-gap);
}

::v-deep .products-page__card.product-mini-card {
  display: block;
  width: calc((100% - var(--products-page-column-gap)) / 2);
  margin: 0 0 var(--products-page-column-gap);
  height: auto;
}

.products-page__load-more {
  display: block;
  width: 100%;
}

::v-deep .products-page__pull-refresh .van-pull-refresh__track {
  min-height: 100%;
}

::v-deep .products-page__pull-refresh .van-pull-refresh__text {
  font-size: 24px;
  line-height: 34px;
  font-weight: 400;
  color: #64748B;
}

@media (max-width: 340px) {
  .products-page__main {
    padding-right: 12px;
    padding-left: 12px;
  }

  .products-page__grid {
    --products-page-column-gap: 8px;
  }

  ::v-deep .products-page__card {
    margin-bottom: var(--products-page-column-gap);
  }

  .products-page__search {
    padding-left: 24px;

    input {
      font-size: 32px;
    }
  }
}
</style>
